import { formatContactAddress } from "../../../../../../../_shared/_logic/contact-addresses"
import {
  buildRichTooltip,
  tooltipField,
  tooltipList,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../../_shared/_logic/rich-tooltips"
import { formatPropertyUnitDisplay } from "../../../../../../../../../_shared/_logic/unit-labels"
import {
  buildContactTag,
  buildContactPointMetaTooltipFields,
  formatPropertyUnitIdentifier,
  groupContactPointsByCustomerId,
} from "../../../../../../../_shared/_logic/contact-quality-tags"
import { formatPropertyCustomerRole } from "../../../../../../../_shared/_logic/customer-roles"
import type { AddressContactTag, AddressUnitBadge } from "../_shared/_logic/address-table-contract"

const MAX_VISIBLE_UNIT_BADGES = 3
const MAX_VISIBLE_CONTACT_TAGS = 3
const UNIT_BADGE_LABEL_MAX_LENGTH = 20

export const sortPropertyLinks = (links: PropertyCustomer[]) =>
  links.slice().sort((left, right) => {
    if (left.is_primary !== right.is_primary)
      return Number(right.is_primary) - Number(left.is_primary)

    const leftCreated = left.created_at ? new Date(left.created_at).getTime() : 0
    const rightCreated = right.created_at ? new Date(right.created_at).getTime() : 0

    return leftCreated - rightCreated
  })

export const buildContactPointsByCustomerId = (points: ContactPoint[]) =>
  groupContactPointsByCustomerId(points ?? [])

const contactValuesForKind = (points: ContactPoint[], kind: ContactPointKind | string) =>
  points
    .filter((point) => String(point.kind).toUpperCase() === String(kind).toUpperCase())
    .map((point) =>
      String(kind).toUpperCase() === "ADDRESS"
        ? formatContactAddress(point.value)
        : (point.value ?? "").trim(),
    )
    .filter(Boolean)

export const unitContactColor = (_points: ContactPoint[]) => "neutral"

export const buildCustomerTooltip = (
  link: PropertyCustomer,
  customerById: Record<string, Customer>,
  pointsByCustomerId: Record<string, ContactPoint[]>,
) => {
  const customer = customerById[link.customer_id]
  const roleLabel = formatPropertyCustomerRole(link.role)
  const contactName = customer?.display_name ?? "Unbekannter Ansprechpartner"
  const points = pointsByCustomerId[link.customer_id] ?? []
  const emails = contactValuesForKind(points, "EMAIL")
  const mobiles = contactValuesForKind(points, "MOBILE")
  const fixedLines = contactValuesForKind(points, "FIXED_LINE")
  const phones = [...mobiles, ...fixedLines]
  const addresses = contactValuesForKind(points, "ADDRESS")
  const hasContactData = emails.length || phones.length || addresses.length

  return buildRichTooltip([
    tooltipTitle(`${contactName} · ${roleLabel}`),
    tooltipField("E-Mail", emails[0]),
    tooltipField("Telefon", phones[0]),
    !hasContactData ? tooltipText("Bitte eine E-Mail-Adresse oder Telefonnummer ergänzen.") : null,
  ])
}

export const truncateUnitBadgeLabel = (value: string) => {
  const normalized = value.trim()

  if (normalized.length <= UNIT_BADGE_LABEL_MAX_LENGTH) return normalized

  return `${normalized.slice(0, UNIT_BADGE_LABEL_MAX_LENGTH - 3).trimEnd()}...`
}

const buildUnitBadgeTooltip = (unitLabel: string, contactTooltip: string) =>
  [buildRichTooltip([tooltipTitle(unitLabel)]), contactTooltip].filter(Boolean).join("<br>")

export const buildUnitBadges = (
  property: Property | undefined,
  links: PropertyCustomer[],
  customerById: Record<string, Customer>,
  contactPoints: ContactPoint[],
): AddressUnitBadge[] => {
  const units = property?.units ?? []
  const pointsByCustomerId = buildContactPointsByCustomerId(contactPoints)
  const sortedLinks = sortPropertyLinks(links)
  const linksByUnitId = sortedLinks.reduce(
    (acc, link) => {
      const key = link.property_unit_id || "__unassigned"
      const list = acc[key] ?? []
      list.push(link)
      acc[key] = list

      return acc
    },
    {} as Record<string, PropertyCustomer[]>,
  )
  const unitRows = units.length
    ? units.map((unit) => ({
        id: unit.id,
        label: formatPropertyUnitDisplay(unit),
        links: linksByUnitId[unit.id] ?? [],
      }))
    : [
        {
          id: "__property",
          label: "Gesamte Liegenschaft",
          links: sortedLinks,
        },
      ]

  const badges = unitRows.map((unit) => {
    const pointList = unit.links.flatMap((link) => pointsByCustomerId[link.customer_id] ?? [])
    const contactTooltip = unit.links.length
      ? (tooltipList(
          "Ansprechpartner",
          unit.links.map(
            (link) =>
              `${customerById[link.customer_id]?.display_name ?? "Unbekannt"} · ${formatPropertyCustomerRole(link.role)}`,
          ),
        ) ?? "")
      : buildRichTooltip([tooltipText("Keine Ansprechpartner verknüpft.")])

    return {
      id: unit.id,
      label: truncateUnitBadgeLabel(unit.label),
      color: unitContactColor(pointList),
      tooltip: buildUnitBadgeTooltip(unit.label, contactTooltip),
    }
  })

  const visibleBadges = badges.slice(0, MAX_VISIBLE_UNIT_BADGES)
  const hiddenBadges = badges.slice(MAX_VISIBLE_UNIT_BADGES)

  if (hiddenBadges.length) {
    visibleBadges.push({
      id: "__more_units",
      label: `${hiddenBadges.length} weitere`,
      color: "#74808c",
      tooltip: buildRichTooltip([
        tooltipTitle(`${hiddenBadges.length} weitere Einheiten`),
        tooltipList(
          "Ausgeblendet",
          unitRows.slice(MAX_VISIBLE_UNIT_BADGES).map((unit) => unit.label),
        ),
      ]),
    })
  }

  return visibleBadges
}

const resolveLinkUnitLabel = (property: Property | undefined, link: PropertyCustomer) =>
  formatPropertyUnitIdentifier(property, link.property_unit_id) ?? "Gesamte Liegenschaft"

export const buildContactTags = (
  property: Property | undefined,
  links: PropertyCustomer[],
  customerById: Record<string, Customer>,
  contactPoints: ContactPoint[],
): AddressContactTag[] => {
  const pointsByCustomerId = buildContactPointsByCustomerId(contactPoints)
  const tags = sortPropertyLinks(links).map((link) =>
    buildContactTag({
      id: link.customer_id,
      customer: customerById[link.customer_id],
      fallbackLabel: customerById[link.customer_id]?.display_name,
      contactPoints: pointsByCustomerId[link.customer_id] ?? [],
      unitLabel: resolveLinkUnitLabel(property, link),
      roleLabel: formatPropertyCustomerRole(link.role),
    }),
  )

  const visibleTags: AddressContactTag[] = tags.slice(0, MAX_VISIBLE_CONTACT_TAGS)
  const hiddenTags = tags.slice(MAX_VISIBLE_CONTACT_TAGS)

  if (hiddenTags.length) {
    visibleTags.push({
      id: "__more_contacts",
      label: `${hiddenTags.length} weitere`,
      icon: "",
      color: "neutral",
      tooltip: buildRichTooltip([
        tooltipList(
          "Weitere Ansprechpartner",
          hiddenTags.map((tag) => `${tag.nameLabel ?? tag.label} · ${tag.roleLabel}`),
        ),
      ]),
      tooltipHtml: true,
    })
  }

  return visibleTags
}
